import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, CircularProgress, Chip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { socialAuth, selectAuthLoading } from "../../redux/slices/authSlice";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 3l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.1 19 12 24 12c3.1 0 5.8 1.1 8 3l5.7-5.7C34.6 6.1 29.6 4 24 4c-7.6 0-14.1 4.3-17.7 10.7z" />
    <path fill="#4CAF50" d="M24 44c5.5 0 10.5-2.1 14.3-5.6l-6.6-5.6C29.6 34.7 26.9 36 24 36c-5.2 0-9.6-3.1-11.3-7.5l-6.5 5C9.8 39.6 16.3 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.8l6.6 5.6C40.5 36.9 44 31 44 24c0-1.3-.1-2.7-.4-3.5z" />
  </svg>
);

const AppleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.365 1.43c0 1.14-.47 2.24-1.21 3.04-.84.9-2.1 1.59-3.17 1.5-.14-1.13.48-2.3 1.17-3.05.81-.89 2.23-1.57 3.21-1.49zM20.4 17.46c-.47 1.08-.7 1.57-1.3 2.52-.84 1.33-2.02 2.98-3.48 3-1.3.01-1.63-.86-3.39-.85-1.76.01-2.12.87-3.42.86-1.46-.02-2.58-1.51-3.42-2.84-2.34-3.67-2.59-7.98-1.14-10.27.93-1.48 2.47-2.37 3.9-2.37 1.46 0 2.37.86 3.58.86 1.17 0 1.88-.86 3.58-.86 1.21 0 2.5.66 3.42 1.8-3 1.65-2.52 5.9.67 7.15z" />
  </svg>
);

// ONE font-weight/size/family, set in exactly one place, for every provider
// button's label — Google and Apple each only ever supply the auth LOGIC
// (via their own SDK, loaded and called like any other script), never any
// of their own rendered UI or text. Nothing here is "Google's button" or
// "Apple's button" with our skin on top of it; it's our own markup calling
// their callback.
const LABEL_SX = { fontSize: 14.5, fontWeight: 400, fontFamily: "inherit", color: "text.primary" };

export const BUTTON_HEIGHT = 44;
export const SocialButtonFace = ({ icon, label, pending }) => (
  <Box
    sx={{
      height: BUTTON_HEIGHT,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 1,
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1.5,
      bgcolor: "background.paper",
    }}
  >
    {pending ? (
      <CircularProgress size={18} />
    ) : (
      <>
        {icon}
        <Typography component="span" sx={LABEL_SX}>
          {label}
        </Typography>
      </>
    )}
  </Box>
);

// Both scripts are loaded the exact same way — on demand, once, cached —
// so neither provider gets special-cased handling.
const scriptPromises = {};
const loadScript = (key, src) => {
  if (scriptPromises[key]) return scriptPromises[key];
  scriptPromises[key] = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return scriptPromises[key];
};
const loadGoogleScript = () => (window.google?.accounts?.id ? Promise.resolve() : loadScript("google", "https://accounts.google.com/gsi/client"));
const loadAppleScript = () => (window.AppleID ? Promise.resolve() : loadScript("apple", "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js"));

// Temporary kill switch — everything below is fully built and wired up
// (SDK loading, init, the actual sign-in handlers). Flip back to false to
// show both buttons as a disabled "Coming Soon" pair again; nothing else
// needs to change.
const SOCIAL_LOGIN_ENABLED = false;

// One "Continue with Google" + "Continue with Apple" pair, used identically
// on both LoginPage and RegisterPage — Google/Apple themselves decide
// whether this is a new or returning customer; the backend resolves that
// (see authController.googleAuth/appleAuth), so there's no separate
// "sign up with X" variant of this component. Either button quietly
// doesn't render at all if its REACT_APP_*_CLIENT_ID env var is unset, so
// this is safe to drop onto both pages before those are actually
// configured.
const SocialAuthButtons = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const loading = useSelector(selectAuthLoading);

  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const appleClientId = process.env.REACT_APP_APPLE_CLIENT_ID;

  const [googleReady, setGoogleReady] = useState(false);
  const [googlePending, setGooglePending] = useState(false);
  const [appleReady, setAppleReady] = useState(false);
  const [applePending, setApplePending] = useState(false);

  const handleSocialResult = async (provider, body) => {
    const result = await dispatch(socialAuth({ provider, ...body }));
    if (socialAuth.fulfilled.match(result)) {
      const role = result.payload.user?.role;
      const roleRoutes = { admin: "/admin", rider: "/rider" };
      navigate(roleRoutes[role] || "/dashboard", { replace: true });
    } else {
      enqueueSnackbar(result.payload || "Sign-in failed", { variant: "error" });
    }
  };

  // Google Identity Services' own callback — initialized once, with no
  // button of theirs ever rendered (see handleGoogleClick below, which
  // triggers this programmatically instead of the user clicking something
  // Google drew).
  useEffect(() => {
    if (!googleClientId) return;
    let cancelled = false;
    loadGoogleScript()
      .then(() => {
        if (cancelled) return;
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: (response) => {
            setGooglePending(false);
            if (response?.credential) {
              handleSocialResult("google", { idToken: response.credential });
            }
          },
          use_fedcm_for_prompt: true,
        });
        setGoogleReady(true);
      })
      .catch(() => {
        /* Google's script failed to load — button just stays hidden */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleClientId]);

  useEffect(() => {
    if (!appleClientId) return;
    loadAppleScript()
      .then(() => {
        window.AppleID.auth.init({
          clientId: appleClientId,
          scope: "name email",
          redirectURI: window.location.origin,
          usePopup: true,
        });
        setAppleReady(true);
      })
      .catch(() => {
        /* Apple's script failed to load — button just stays hidden */
      });
  }, [appleClientId]);

  const handleGoogleClick = () => {
    if (!SOCIAL_LOGIN_ENABLED || !googleReady) return;
    setGooglePending(true);
    // One Tap's own prompt UI — not a button, just a momentary native
    // card — is what actually surfaces the account picker; if the browser
    // has it suppressed (the person dismissed it recently, Google's own
    // cooldown), nothing shows and this notification is how we find out,
    // so the spinner doesn't spin forever with no explanation.
    window.google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        setGooglePending(false);
        enqueueSnackbar("Google sign-in isn't available right now — please try again in a moment.", {
          variant: "warning",
        });
      }
    });
  };

  const handleAppleClick = async () => {
    if (!SOCIAL_LOGIN_ENABLED || !appleReady) return;
    setApplePending(true);
    try {
      const res = await window.AppleID.auth.signIn();
      const idToken = res?.authorization?.id_token;
      // Apple only ever includes this `user` object on the VERY FIRST
      // authorization for a given account — must be captured and
      // forwarded now, since it'll never be sent again on later sign-ins.
      const firstName = res?.user?.name?.firstName;
      const lastName = res?.user?.name?.lastName;
      if (idToken) {
        await handleSocialResult("apple", { idToken, firstName, lastName });
      }
    } catch (err) {
      // User closed the popup / cancelled — Apple rejects the promise for
      // this too, not a genuine error worth surfacing.
      if (err?.error !== "popup_closed_by_user") {
        enqueueSnackbar("Apple sign-in failed", { variant: "error" });
      }
    } finally {
      setApplePending(false);
    }
  };

  // While disabled, show both buttons as "Coming Soon" even if their client
  // IDs aren't configured yet / the SDKs haven't finished loading — only
  // hide a button once the feature is actually live and genuinely has no
  // client ID to work with.
  const showGoogle = googleReady || !SOCIAL_LOGIN_ENABLED;
  const showApple = appleReady || !SOCIAL_LOGIN_ENABLED;

  if (SOCIAL_LOGIN_ENABLED && !googleClientId && !appleClientId) return null;

  const googleDisabled = !SOCIAL_LOGIN_ENABLED || loading || googlePending;
  const appleDisabled = !SOCIAL_LOGIN_ENABLED || loading || applePending;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
      {!SOCIAL_LOGIN_ENABLED && (showGoogle || showApple) && (
        <Chip
          label="Coming Soon"
          size="small"
          sx={{
            alignSelf: "center",
            mb: 0.25,
            bgcolor: "rgba(0,166,178,0.1)",
            color: "#00838c",
            fontWeight: 700,
            letterSpacing: 0.5,
          }}
        />
      )}

      {showGoogle && (
        <Box
          component="button"
          type="button"
          disabled={googleDisabled}
          onClick={handleGoogleClick}
          sx={{
            p: 0,
            m: 0,
            border: 0,
            width: "100%",
            background: "none",
            cursor: "pointer",
            opacity: googleDisabled ? 0.6 : 1,
            "&:disabled": { cursor: "default" },
          }}
        >
          <SocialButtonFace icon={<GoogleIcon />} label="Continue with Google" pending={googlePending} />
        </Box>
      )}

      {showApple && (
        <Box
          component="button"
          type="button"
          disabled={appleDisabled}
          onClick={handleAppleClick}
          sx={{
            p: 0,
            m: 0,
            border: 0,
            width: "100%",
            background: "none",
            cursor: "pointer",
            opacity: appleDisabled ? 0.6 : 1,
            "&:disabled": { cursor: "default" },
          }}
        >
          <SocialButtonFace icon={<AppleIcon />} label="Continue with Apple" pending={applePending} />
        </Box>
      )}
    </Box>
  );
};

export default SocialAuthButtons;
